import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  CreditCard, 
  Truck, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft, 
  ShieldCheck, 
  ShoppingBag,
  Lock,
  ArrowRight,
  Loader2,
  Globe
} from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { Link, useNavigate } from 'react-router-dom';

interface CheckoutStep {
  id: number;
  title: string;
  icon: any;
}

const steps: CheckoutStep[] = [
  { id: 1, title: 'Shipping', icon: Truck },
  { id: 2, title: 'Review & Verify', icon: ShieldCheck }
];

export const MultiStepCheckout: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const { products, resolveMediaUrl, mission, updateMission, addOrder, financialSettings } = useBrand();
  const { cart, cartTotal, clearCart, calculateDiscount } = useCart();
  const { user, updateProfile, isMember, isAdmin } = useUser();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const [shippingInfo, setShippingInfo] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    street: '',
    city: '',
    province: '',
    country: 'Canada',
    postalCode: ''
  });

  useEffect(() => {
    if (cart.length === 0 && !isComplete) {
      navigate('/shop');
    }
  }, [cart, isComplete, navigate]);

  // Determine if buyer is Canadian vs International
  const isCanadianBuyer = (() => {
    const c = (shippingInfo.country || 'Canada').trim().toLowerCase();
    return c === 'canada' || c === 'ca' || c === 'can' || c === 'ca-on' || c === 'cad';
  })();

  // Real cart data based on Syndicate Financial Protocol
  const isSyndicateMember = isMember || isAdmin;
  const subtotal = cartTotal;
  const isPreOrderCart = cart.some(item => 
    (item as any).type === 'preorder' ||
    (item.id && (item.id.includes('preorder') || item.id.includes('cx2') || item.id.includes('c2'))) ||
    (item.category && (item.category === 'Vessels' || item.category === 'Boards' || item.category.toLowerCase().includes('pre-order') || item.category.toLowerCase().includes('preorder'))) ||
    (item.name && (item.name.toLowerCase().includes('c2') || item.name.toLowerCase().includes('cx2') || item.name.toLowerCase().includes('pre-order') || item.name.toLowerCase().includes('preorder') || item.name.toLowerCase().includes('vessel') || item.name.toLowerCase().includes('board') || item.name.toLowerCase().includes('expedition')))
  );
  const hasShipping = cart.some(item => item.type !== 'membership') && !isPreOrderCart;
  const shipping = (hasShipping && !isSyndicateMember) ? (financialSettings?.shippingRate || 0) : 0;
  // Tax is only applied to Canadian buyers (13% HST). International buyers are 0% tax.
  const taxRate = isCanadianBuyer ? (financialSettings?.taxRate ?? 13) : 0;
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + shipping + tax;

  const handleNext = () => {
    if (currentStep < 2) {
      if (currentStep === 1 && (!shippingInfo.name || !shippingInfo.email || !shippingInfo.street || !shippingInfo.phone)) {
        alert("Please complete all shipping and contact protocols.");
        return;
      }
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsProcessing(true);
    
    try {
      const checkoutItems = cart.map(item => {
        const discountAmount = calculateDiscount(item);
        const finalPrice = Math.max(0, item.price - discountAmount);
        return {
          id: item.id,
          name: item.name,
          price: finalPrice,
          quantity: item.quantity,
          image: item.image,
          category: item.category,
          isVessel: item.category === 'Vessels' || item.category === 'Boards' || item.isVessel || item.name.toLowerCase().includes('c2') || item.name.toLowerCase().includes('vessel') || item.name.toLowerCase().includes('board') || item.name.toLowerCase().includes('endurance')
        };
      });

      const isVesselOrder = checkoutItems.some(i => 
        i.isVessel ||
        i.name.toLowerCase().includes('vessel') || 
        i.name.toLowerCase().includes('endurance') ||
        i.name.toLowerCase().includes('board') ||
        i.id.includes('c2')
      );

      try {
        // If total is 0 (e.g., complimentary item), skip Stripe and process immediately
        if (total === 0) {
          console.log("Processing complimentary order (total is 0)...");
          await addOrder({
            userId: user?.uid || 'guest',
            userEmail: shippingInfo.email || user?.email || '',
            userName: shippingInfo.name || user?.displayName || 'Valued Client',
            items: checkoutItems,
            type: isVesselOrder ? 'vessel_reservation' : 'shop_order',
            total: 0,
            subtotal: 0,
            tax: 0,
            shipping: 0,
            shippingAddress: shippingInfo
          });
          
          await new Promise(resolve => setTimeout(resolve, 1500));
          clearCart();
          setIsComplete(true);
          setIsProcessing(false);
          return;
        }

        let response;
        let retries = 3;
        let lastError = null;

        while (retries > 0) {
          try {
            response = await fetch('/api/create-shop-checkout-session', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: checkoutItems,
                userId: user?.uid || 'guest',
                email: shippingInfo.email || user?.email,
                shippingInfo,
                tax,
                shipping,
                type: isVesselOrder ? 'shop_vessel' : 'shop'
              }),
            });
            // If it's a success, or a 4xx error (which is likely a validation error, not a network hiccup), break
            if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 404)) {
              break;
            }
          } catch (err) {
            lastError = err;
          }
          retries--;
          if (retries > 0) await new Promise(res => setTimeout(res, 1500));
        }

        if (!response || !response.ok) {
          if (response && response.status === 404) {
             throw new Error("Protocol Failure 404: The secure service endpoint could not be reached. If you have an ad-blocker or strict privacy shield, try disabling it for this site.");
          }
          const errData = response ? await response.json().catch(() => ({})) : {};
          throw new Error(errData.error || `Protocol Failure: ${response?.status || 'Network Disconnected'}`);
        }

        const session = await response.json();
        if (session.url) {
          setCheckoutUrl(session.url);
          
          // Proper delay before redirect attempt
          setTimeout(() => {
            const stripeUrl = session.url;
            console.log("Initiating secure redirect to:", stripeUrl);
            window.location.href = stripeUrl;
          }, 100);
          return;
        }
      } catch (e: any) {
        console.error("Stripe checkout failed:", e);
        
        // ONLY fallback for $0 total or if explicitly allowed
        if (total === 0) {
          await addOrder({
            userId: user?.uid || 'guest',
            userEmail: shippingInfo.email || user?.email || '',
            userName: shippingInfo.name || user?.displayName || 'Valued Client',
            items: checkoutItems,
            total: 0,
            subtotal: 0,
            tax: 0,
            shipping: 0,
            shippingAddress: shippingInfo
          });

          await new Promise(resolve => setTimeout(resolve, 2000));
          clearCart();
          setIsComplete(true);
          setIsProcessing(false);
          return;
        }

        alert(`Payment Terminal Error: ${e.message || 'The secure connection could not be established.'}`);
        setIsProcessing(false);
        return;
      }
    } catch (error: any) {
      console.error("Checkout failed:", error);
      setIsProcessing(false);
      // We could add an error state here
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-ows-sand flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white p-8 md:p-12 rounded-[2rem] md:rounded-[3rem] shadow-2xl text-center space-y-8 border border-ows-teal/5"
        >
          <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="h-12 w-12 text-ows-teal" />
          </div>
          <h2 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark">Expedition Confirmed</h2>
          <p className="text-sm text-ows-dark/60 italic font-serif leading-relaxed">
            "Your gear is being prepared for the journey. A confirmation dispatch has been sent to your terminal."
          </p>
          <div className="pt-8 space-y-4">
            <Link 
              to="/member-portal" 
              className="w-full block py-5 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-teal transition-all rounded-2xl"
            >
              Go to Member Portal
            </Link>
            <Link 
              to="/" 
              className="w-full block py-5 bg-ows-sand text-ows-dark text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-teal hover:text-white transition-all rounded-2xl"
            >
              Return to Base
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ows-sand pt-24 md:pt-32 pb-16 md:pb-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10 sm:mb-16 text-center">
          <h1 className="text-2xl sm:text-4xl font-display font-light uppercase tracking-[0.15em] sm:tracking-[0.2em] text-ows-dark mb-8 sm:mb-12">Secure Checkout</h1>
          
          {/* Progress Bar */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 max-w-lg mx-auto relative">
            {steps.map((step, i) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-2 sm:gap-3 relative z-10">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                    currentStep >= step.id ? 'bg-ows-teal text-white shadow-lg shadow-ows-teal/20' : 'bg-white text-ows-dark/20 border border-ows-teal/10'
                  }`}>
                    <step.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className={`text-[8px] font-bold uppercase tracking-widest ${
                    currentStep >= step.id ? 'text-ows-teal' : 'text-ows-dark/20'
                  }`}>{step.title}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className="w-12 sm:w-24 h-[1px] bg-ows-teal/10 relative -top-3 sm:-top-4">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: currentStep > step.id ? '100%' : '0%' }}
                      className="h-full bg-ows-teal"
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Form Area */}
          <div className="lg:col-span-7">
            <div className={`bg-white p-5 sm:p-8 md:p-16 rounded-[1.5rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-xl border border-ows-teal/5 ${isProcessing ? 'flex items-center justify-center min-h-[350px] sm:min-h-[400px]' : ''}`}>
              <AnimatePresence mode="wait">
                {isProcessing && !checkoutUrl && (
                  <motion.div 
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center space-y-6 sm:space-y-8"
                  >
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto">
                      <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 text-ows-teal animate-spin" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-display font-light uppercase tracking-[0.2em] sm:tracking-[0.3em] text-ows-dark">Establishing Secure Protocol</h2>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Encrypting data for mission terminal...</p>
                  </motion.div>
                )}

                {checkoutUrl && (
                  <motion.div 
                    key="redirecting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center space-y-6 sm:space-y-8 py-6 sm:py-10"
                  >
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto animate-bounce">
                      <ShieldCheck className="h-10 w-10 sm:h-12 sm:w-12 text-ows-teal" />
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <h2 className="text-lg sm:text-xl font-display font-light uppercase tracking-[0.2em] sm:tracking-[0.3em] text-ows-dark">Authorization Ready</h2>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 max-w-sm mx-auto leading-relaxed">
                        Session encrypted. If the secure gateway didn't open automatically, use the button below to proceed.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:gap-4 pt-2 sm:pt-4">
                      <button 
                        onClick={() => {
                          window.location.href = checkoutUrl;
                        }}
                        className="w-full sm:w-auto px-8 sm:px-12 py-5 sm:py-6 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] hover:bg-ows-dark transition-all rounded-2xl flex items-center justify-center gap-3 shadow-2xl shadow-ows-teal/20 mx-auto"
                      >
                        Open Secure Checkout <ArrowRight className="h-4 w-4" />
                      </button>

                      <a 
                        href={checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[9px] text-ows-dark/30 uppercase tracking-widest hover:text-ows-teal transition-colors underline"
                      >
                        Open in New Tab (Last Resort)
                      </a>
                    </div>
                  </motion.div>
                )}

                {!isProcessing && currentStep === 1 && (
                  <motion.div 
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6 sm:space-y-8"
                  >
                    <h2 className="text-lg sm:text-xl font-display font-light uppercase tracking-widest mb-6 sm:mb-8">Shipping Information</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div className="sm:col-span-2 space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Full Name</label>
                        <input 
                          type="text" 
                          value={shippingInfo.name || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, name: e.target.value }))}
                          autoComplete="name"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="Marcus Thorne" 
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Email Address</label>
                        <input 
                          type="email" 
                          value={shippingInfo.email || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, email: e.target.value }))}
                          autoComplete="email"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="marcus@explorer.com" 
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Contact Phone</label>
                        <input 
                          type="tel" 
                          value={shippingInfo.phone || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, phone: e.target.value }))}
                          autoComplete="tel"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="+1 (555) 000-0000" 
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Street Address</label>
                        <input 
                          type="text" 
                          value={shippingInfo.street || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, street: e.target.value }))}
                          autoComplete="address-line1"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="123 Expedition Way" 
                        />
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">City</label>
                        <input 
                          type="text" 
                          value={shippingInfo.city || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                          autoComplete="address-level2"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="Toronto" 
                        />
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Province / State</label>
                        <input 
                          type="text" 
                          value={shippingInfo.province || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, province: e.target.value }))}
                          autoComplete="address-level1"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="Ontario" 
                        />
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        <div className="flex items-center justify-between ml-1 sm:ml-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Country</label>
                          <span className={`text-[8px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                            isCanadianBuyer ? 'bg-ows-teal/10 text-ows-teal' : 'bg-ows-orange/10 text-ows-orange'
                          }`}>
                            {isCanadianBuyer ? '13% Sales Tax (Canada)' : '0% Tax (International)'}
                          </span>
                        </div>
                        <select
                          value={['Canada', 'United States', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'Mexico'].includes(shippingInfo.country) ? shippingInfo.country : 'Other'}
                          onChange={(e) => {
                            if (e.target.value !== 'Other') {
                              setShippingInfo(prev => ({ ...prev, country: e.target.value }));
                            } else {
                              setShippingInfo(prev => ({ ...prev, country: 'International' }));
                            }
                          }}
                          autoComplete="country-name"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm font-semibold text-ows-dark"
                        >
                          <option value="Canada">🇨🇦 Canada (13% Sales Tax Applies)</option>
                          <option value="United States">🇺🇸 United States (0% Sales Tax - International)</option>
                          <option value="United Kingdom">🇬🇧 United Kingdom (0% Sales Tax - International)</option>
                          <option value="Australia">🇦🇺 Australia (0% Sales Tax - International)</option>
                          <option value="Germany">🇩🇪 Germany (0% Sales Tax - International)</option>
                          <option value="France">🇫🇷 France (0% Sales Tax - International)</option>
                          <option value="Japan">🇯🇵 Japan (0% Sales Tax - International)</option>
                          <option value="Mexico">🇲🇽 Mexico (0% Sales Tax - International)</option>
                          <option value="Other">🌐 Other International Country (0% Tax)</option>
                        </select>
                        {!['Canada', 'United States', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'Mexico'].includes(shippingInfo.country) && (
                          <input 
                            type="text" 
                            value={shippingInfo.country}
                            onChange={(e) => setShippingInfo(prev => ({ ...prev, country: e.target.value }))}
                            autoComplete="country-name"
                            className="w-full mt-2 px-4 sm:px-8 py-3 sm:py-4 rounded-xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-xs font-semibold" 
                            placeholder="Specify Country Name" 
                          />
                        )}
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-1 sm:ml-2">Postal Code</label>
                        <input 
                          type="text" 
                          value={shippingInfo.postalCode || ''}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, postalCode: e.target.value }))}
                          autoComplete="postal-code"
                          className="w-full px-4 sm:px-8 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl bg-ows-sand/20 border border-ows-teal/5 focus:ring-2 focus:ring-ows-teal transition-all text-sm" 
                          placeholder="A1A 1A1" 
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {!isProcessing && currentStep === 2 && (
                  <motion.div 
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6 sm:space-y-8"
                  >
                    <div className="flex items-center justify-between mb-6 sm:mb-8">
                      <h2 className="text-lg sm:text-xl font-display font-light uppercase tracking-widest">Final Dispatch Review</h2>
                      <div className="px-3 py-1 bg-ows-teal/10 rounded-full">
                        <span className="text-[8px] font-bold text-ows-teal uppercase tracking-widest font-mono">Channel: Secure</span>
                      </div>
                    </div>

                    <div className="space-y-4 sm:space-y-6">
                      <div className="p-5 sm:p-8 bg-ows-sand/20 rounded-[1.5rem] sm:rounded-[2rem] border border-ows-teal/5 shadow-sm">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                          <h3 className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Fulfillment Target</h3>
                          <button onClick={() => setCurrentStep(1)} className="text-[8px] font-bold uppercase tracking-widest text-ows-teal hover:underline">Revise</button>
                        </div>
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-xl flex items-center justify-center text-ows-teal shrink-0">
                            <Truck className="h-4 w-4 sm:h-5 sm:w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-ows-dark truncate">{shippingInfo.name || 'Marcus Thorne'}</p>
                            <p className="text-xs text-ows-dark/60 mt-1 leading-relaxed">{shippingInfo.street}, {shippingInfo.city}, {shippingInfo.province ? shippingInfo.province + ', ' : ''}{shippingInfo.postalCode}, {shippingInfo.country}</p>
                            <p className="text-xs text-ows-dark/40 mt-1 font-mono uppercase tracking-widest truncate">{shippingInfo.email} • {shippingInfo.phone}</p>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 sm:p-6 bg-ows-orange/5 border border-ows-orange/10 rounded-2xl flex items-start gap-3 sm:gap-4">
                        <Lock className="h-5 w-5 text-ows-orange shrink-0 mt-0.5" />
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-orange leading-relaxed">
                          Secure Payment Hand-Off: You will be redirected to the Syndicate's encrypted payment terminal (Stripe) to finalize the transaction.
                        </p>
                      </div>

                      {!isCanadianBuyer && (
                        <div className="p-4 sm:p-6 bg-ows-teal/10 border border-ows-teal/20 rounded-2xl flex items-start gap-3 sm:gap-4">
                          <Globe className="h-5 w-5 text-ows-teal shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">
                              International Customs & Duties Policy
                            </p>
                            <p className="text-[10px] text-ows-dark/70 font-mono leading-relaxed">
                              0% Canadian sales tax applied. Please note: Open Water Syndicate is <strong>not responsible for local customs duties, import tariffs, or clearance fees</strong>. Destination duties and taxes are the sole responsibility of the international recipient upon delivery.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {!isProcessing && (
                <div className="mt-8 sm:mt-16 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-6 sm:pt-12 border-t border-ows-teal/5">
                  <button 
                    onClick={handleBack}
                    disabled={currentStep === 1 || isProcessing}
                    className={`flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest transition-all py-3 sm:py-0 ${
                      currentStep === 1 ? 'hidden sm:flex opacity-0 pointer-events-none' : 'text-ows-dark/40 hover:text-ows-dark'
                    }`}
                  >
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                  <button 
                    onClick={handleNext}
                    disabled={isProcessing}
                    id="checkout-next-button"
                    className="w-full sm:w-auto px-6 sm:px-12 py-4 sm:py-5 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] hover:bg-ows-teal transition-all rounded-2xl flex items-center justify-center gap-3 disabled:opacity-50 shadow-2xl"
                  >
                    {isProcessing ? (
                      <>Processing...</>
                    ) : (
                      <>
                        {currentStep === 2 ? 'Authorize & Dispatch' : 'Continue to Review'} 
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary Area */}
          <div className="lg:col-span-5">
            <div className="bg-ows-dark text-white p-5 sm:p-8 md:p-12 rounded-[1.5rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl sticky top-28 sm:top-32 overflow-hidden">
              <h2 className="text-base sm:text-lg md:text-xl font-display font-light uppercase tracking-widest mb-6 sm:mb-8 md:mb-12 flex items-center gap-3">
                <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5 text-ows-teal" /> Order Summary
              </h2>
              
              <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-12">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-4 sm:gap-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/5 rounded-2xl overflow-hidden flex-shrink-0">
                      <img src={resolveMediaUrl(item.image) || undefined} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold uppercase tracking-widest mb-1 truncate">{item.name}</h4>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">{item.category}</p>
                      {Array.isArray(item.items) && item.items.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {item.items.map((subItem, idx) => (
                            <span key={`${item.id}-sub-${idx}`} className="text-[7px] px-1.5 py-0.5 bg-white/5 rounded border border-white/10 text-white/60 uppercase tracking-widest">
                              {subItem}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs sm:text-sm font-bold text-ows-teal mt-1 sm:mt-2">${item.price} x {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 sm:space-y-4 border-t border-white/10 pt-6 sm:pt-8 mb-6 sm:mb-8">
                <div className="flex justify-between text-xs">
                  <span className="text-white/40 uppercase tracking-widest">Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40 uppercase tracking-widest">Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                {cart.some(item => item.category === 'Vessels' || item.category === 'Boards' || item.name?.toLowerCase().includes('board') || item.name?.toLowerCase().includes('vessel') || item.name?.toLowerCase().includes('c2') || item.name?.toLowerCase().includes('endurance')) && (
                  <div className="p-3.5 sm:p-4 bg-ows-orange/10 border border-ows-orange/20 rounded-xl mt-2 text-[9px] uppercase tracking-wider text-ows-orange font-mono leading-relaxed">
                    <strong>DELIVERY & PICKUP:</strong> $100 CAD flat rate Canada-wide delivery anywhere in Canada, or free local pickup directly in Toronto, Ontario. Shipping is not charged during the deposit phase — your remaining 70% balance and shipping are collected when your unit is ready at our Toronto, Ontario facility.
                  </div>
                )}
                <div className="flex justify-between text-xs items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 uppercase tracking-widest">Tax</span>
                    {!isCanadianBuyer && (
                      <span className="text-[8px] font-mono px-2 py-0.5 bg-ows-teal/20 text-ows-teal rounded font-bold uppercase tracking-wider">
                        0% Intl Exempt
                      </span>
                    )}
                  </div>
                  <span>${tax.toFixed(2)}</span>
                </div>
                {!isCanadianBuyer && (
                  <div className="p-3.5 sm:p-4 bg-white/5 border border-white/10 rounded-xl mt-2 text-[9px] uppercase tracking-wider text-white/70 font-mono leading-relaxed space-y-1">
                    <div className="flex items-center gap-1.5 text-ows-teal font-bold">
                      <Globe className="h-3.5 w-3.5 shrink-0" />
                      <span>INTERNATIONAL DUTY NOTICE:</span>
                    </div>
                    <p>
                      Canadian sales tax is 0% for buyers outside Canada. Open Water Syndicate is <strong>not responsible for local customs duties, VAT, or import tariffs</strong>. All destination import fees are the sole responsibility of the international buyer upon arrival.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-end mb-8 sm:mb-12">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-ows-teal block mb-1">Total Due</span>
                  {cart.some(item => item.name.toLowerCase().includes('c2')) && (
                    <span className="text-[8px] font-bold uppercase tracking-widest text-ows-orange font-mono">Includes Pre-Order Allocation</span>
                  )}
                </div>
                <span className="text-3xl sm:text-4xl font-display font-light tracking-widest">${total.toFixed(2)}</span>
              </div>

              <div className="flex items-center gap-3 p-4 sm:p-6 bg-white/5 rounded-2xl border border-white/5">
                <Lock className="h-4 w-4 text-ows-teal shrink-0" />
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 leading-relaxed">
                  Secure encrypted transaction. Your data is protected by Syndicate security protocols.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
